import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import { RANGE_SELECTOR_MODELS } from '../../../../shared/screener-constants';

import {
  UPDATE_MSTAR_FILTER,
  UPDATE_RANGE_FILTER,
  UPDATE_ASSET_FILTER,
  UPDATE_STYLE_BOX_FILTER,
  UPDATE_PRODUCT_FILTER,
  UPDATE_FUNDTYPE_FILTER,
  UPDATE_PRESET_CATEGORY,
  UPDATE_PLAN_NUMBER,
  FILTER_PLAN_HOLDINGS,
  RESET_FILTERS,
  RESET_HOLDINGS_CONTEXT,
  SET_INITIAL_STATE,
  SHOW_ONLY_MY_HOLDINGS,
  REDUCE_ARGS,
  SHOW_ONLY_TIAA,
  SHOW_NULL_DATA,
  UPDATE_PARTICIPANT_HOLDINGS,
  UPDATE_SEARCHED_HOLDINGS,
  FETCH_PLAN_SUCCESS,
  FETCH_PLAN_FAILURE,
  FETCH_PLAN_REQUEST,
  REDUCE_ARGS_DISTRIBUTIONS
} from '../../actions/action-types';

import {
  FIELDS,
  MODEL_TYPES,
  ASSET_CLASS_MODELS,
  PRODUCT_TYPE_MODELS,
  STYLE_BOX_MODELS,
  CHANNELS,
  SHARE_CLASS,
  PRODUCT_TYPE_TO_MODEL,
  ASSET_CLASS_TO_MODEL,
  DISTPERIODS,
  DEFAULT_VIEWS
} from '../../../../shared/screener-constants';

import {
  rangeSelectorModels,
  styleBoxModels,
  assetClassModels,
  productTypeModels,
  presetCategoryModels
} from '../../../../models/screener';

import Conditions from '../helpers/conditions';

import {
  formatShareClassArgs,
  formatFundTypeArgs
} from '../helpers/fundType-arguments';

import { formatArgs } from '../../actions/screener-args';

import { queryStringMap } from '../../../../utils/result-tags';

const publicDefaultProductTypes = [
  PRODUCT_TYPE_MODELS.FIXEDANNUITIES,
  PRODUCT_TYPE_MODELS.VARIABLEANNUITIES,
  PRODUCT_TYPE_MODELS.CREF,
  PRODUCT_TYPE_MODELS.TIAAACCESS,
  PRODUCT_TYPE_MODELS.TIAAREALESTATE,
  PRODUCT_TYPE_MODELS.MUTUALFUNDS
];

const publicDefaultAssetClasses = [
  ASSET_CLASS_MODELS.ALL,
  ASSET_CLASS_MODELS.EQUITIES,
  ASSET_CLASS_MODELS.FIXEDINCOME,
  ASSET_CLASS_MODELS.GUARANTEED,
  ASSET_CLASS_MODELS.MONEYMARKET,
  ASSET_CLASS_MODELS.MULTIASSET,
  ASSET_CLASS_MODELS.OTHER,
  ASSET_CLASS_MODELS.REALESTATE,
  ASSET_CLASS_MODELS.TARGETDATE
];

/*Filter reducer is the largest screener file (and would benefit from being broken out into smaller files.)
  The first set of functions *HELPERS* are all helpers for managing the filters
  Next is the actual REDUCER, made up of many cases that are triggered by filter-actions.js */

/*The 'filters' part of the state is used to update the call made to the screener, so the reducer manages those changes
  REDUCE ARGS is the final step of any filter change, it finalizes the arguments sent to the screener ('args' in the state)
  The rest of the keys in the state are used to keep track of and manage the info needed to operate the filters */
export const getInitialState = () => {
  return {
    inputs: {
      searchedPlanNumber: '',
      isSearchedPlanInContext: false,
      isPlanDataLoaded: false,
      subPlan: '',
      distPeriod: DISTPERIODS.YEAR
    },
    toggles: {
      showOnlyMyHoldings: false
    },
    planInfo: [],
    planNumbers: [],
    planDisclosures: {},
    planApiLoading: false,
    planApiError: '',
    nonDefaultActiveFilters: [],
    participantHoldings: [],
    participantPlansAndTickers: [],
    searchedHoldings: [],
    productTypesFromPlan: [],
    assetClassesFromPlan: [],

    filters: {
      MStarFilterArray: [false, false, false, false, false],
      MStarValue: -1,
      MStarValues: [],
      presetCategories: presetCategoryModels(),
      rangeSelectors: rangeSelectorModels(),
      assetClassFilters: assetClassModels(),
      productTypeFilters: productTypeModels(),
      styleBoxes: styleBoxModels(),
      planXids: [],
      showOnlyTIAA: false,
      showFundsWithNullData: false
    },
    args: {
      appliedFilters: {},
      fundTypeArgs: {}
    }
  };
};

/**HELPERS**/
/**
 * Checks if key is in nested object in array
 * @param {array} cards - array of card objects.
 * @param {string} key - key to be compared.
 */
const getCheckedBoolean = (cards, key) => {
  let bool = false;
  cards.forEach(card => {
    if (card.mappings.modelKey === key) {
      bool = true;
    }
  });
  return bool;
};

/**
 * Sets all values of an object's key
 * @param {object} object - object to be updated.
 * @param {string} keyName - key to be updated.
 * @param {value} value - value to set.
 */
const setObjectValues = (object, keyName, value) => {
  for (const key of Object.keys(object)) {
    object[key] = {
      ...object[key],
      [keyName]: value
    };
  }
  return object;
};

const setFilterAllCheckBoxes = (filter, value) => {
  Object.keys(filter).forEach(key => {
    filter[key].checked = value;
  });
};

const resetPresetCategories = categories => {
  categories?.forEach(category => {
    category?.cards?.forEach(card => {
      card.checked = false;
    });
  });
  return categories;
};

/**
 * Activate filters based on an array of types
 * @param {object} filters - filters to update.
 * @param {array} filtersToActivate - array of filter model keys.
 */
export const activateFilters = (filters, filtersToActivate) => {
  Object.values(filters).forEach(filter => {
    const shouldCheck =
      filtersToActivate.indexOf(filter.modelKey) > -1;
    filter.checked = shouldCheck;

    return filter;
  });
};

/**
 * Get array of product model keys based on product types array
 * @param {array} productTypesArray - array of PRODUCT_TYPES.
 */
export const getModelKeys = (typesArray, mapToModels) => {
  const ModelKeys = typesArray.map(type => {
    return mapToModels[type];
  });
  return ModelKeys;
};

export const updateActiveFilters = (state, shouldActivate, filterName) => {
  const nonDefaultActiveFilters = [...state.nonDefaultActiveFilters];
  if (shouldActivate) {
    if (!nonDefaultActiveFilters.includes(filterName)) {
      nonDefaultActiveFilters.push(filterName);
    }
  } else {
    return nonDefaultActiveFilters.filter(el => el !== filterName);
  }
  return nonDefaultActiveFilters;
};


const checkVariableAnnuitySubTypes = (productTypeFilters, checked) => {
  const result = {
    ...productTypeFilters,
    [PRODUCT_TYPE_MODELS.CREF]: {
      ...productTypeFilters[PRODUCT_TYPE_MODELS.CREF],
      checked,
      currentOption: SHARE_CLASS.CLASSR3
    },
    [PRODUCT_TYPE_MODELS.TIAAACCESS]: {
      ...productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS],
      checked,
      currentOption: SHARE_CLASS.LEVEL1
    },
    [PRODUCT_TYPE_MODELS.TIAAREALESTATE]: {
      ...productTypeFilters[PRODUCT_TYPE_MODELS.TIAAREALESTATE],
      checked
    }
  };
  return result;
};


export const updateRangeFilter = (state, modelKey, index, value = null) => {
  const rangeSelectors = { ...state.filters.rangeSelectors };
  const filter = { ...rangeSelectors[modelKey] };
  const selectors = filter.selectors;

  selectors[index] = value || !selectors[index];
  rangeSelectors[modelKey] = filter;

  return {
    rangeSelectors,
    shouldActivate: selectors.includes(true)
  };
};

export const updateExpenseFilter = (state, modelKey, index, value = null) => {

  const rangeSelectors = { ...state.filters.rangeSelectors };
  const filter = { ...rangeSelectors[modelKey] };
  const selectors = filter.selectors;

  selectors[index] = value || !selectors[index];
  rangeSelectors[modelKey] = filter;

  return {
    rangeSelectors,
    shouldActivate: selectors.includes(true)
  };
};

/**
 * Returns asset class filters object
 * @param {object} state - application state.
 * @param {string} modelKey - string name for the model.
 * @param {array} cards - array of card objects.
 */
export const updateAssetClassFilter = (state, modelKey, cards = []) => {
  let assetClassFilters = {
    ...state.filters.assetClassFilters
  };

  //get the specific model
  const filter = {
    ...assetClassFilters[modelKey]
  };

  //If cards has length, the preset categories are making this update,
  //which cards are checked affects the filter
  if (cards.length) {
    const checkedCards = cards.filter(card => card.checked === true);
    for (const key of Object.keys(assetClassFilters)) {
      const isChecked = getCheckedBoolean(checkedCards, key);
      assetClassFilters[key] = {
        ...assetClassFilters[key],
        checked: isChecked
      };
    }
  } else {
    filter.checked = !filter.checked;

    // Update the filter model
    assetClassFilters[modelKey] = filter;

    //we want to turn them all on or off
    if (modelKey === ASSET_CLASS_MODELS.ALL) {
      if (filter.checked === true) {
        assetClassFilters = setObjectValues(assetClassFilters, 'checked', true);
      } else {
        assetClassFilters = setObjectValues(
          assetClassFilters,
          'checked',
          false
        );
      }
    }
  }

  return assetClassFilters;
};

//TIAA-13458, Re-designed Screener: Add Asset Class Types to '?bluetagcharacteristic'
export const updateAssetClassFilterByBlueTag = (state, modelKey) => {
  setFilterAllCheckBoxes(state.filters.productTypeFilters, true);
  state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.CREF].currentOption = SHARE_CLASS.ALL;
  state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].currentOption = SHARE_CLASS.ALL;
  state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.ALL;

  Object.keys(state.filters.assetClassFilters).forEach(key => {
    state.filters.assetClassFilters[key].checked = (key === modelKey);
  });

};

export const updateSytleBoxesByBlueTag = (modelKey) => {

  let selecter = [];
  switch (modelKey) {
    case 'longduration':
    case 'equitygrowth':
      selecter = [
        [false, false, true],
        [false, false, true],
        [false, false, true]
      ];
      break;

    case 'mediumduration':
    case 'equityblend':
      selecter = [
        [false, true, false],
        [false, true, false],
        [false, true, false]
      ];
      break;
    case 'shortduration':
    case 'equityvalue':
      selecter = [
        [true, false, false],
        [true, false, false],
        [true, false, false]
      ];
      break;
    case 'lowcreditquality':
    case 'equitysmall':
      selecter = [
        [false, false, false],
        [false, false, false],
        [true, true, true]
      ];
      break;
    case 'moderatecreditquality':
    case 'equitymid':
      selecter = [
        [false, false, false],
        [true, true, true],
        [false, false, false]
      ];
      break;
    case 'highcreditquality':
    case 'equitylarge':
      selecter = [
        [true, true, true],
        [false, false, false],
        [false, false, false]
      ];
      break;
    default:
      break;
  }

  return selecter;

};
/* When plan in context, use updateProductTypeFilter */
export const updateProductTypeFilter = (state, modelKey, cards = []) => {
  const productTypeFilters = {
    ...state.filters.productTypeFilters
  };
  const filter = {
    ...productTypeFilters[modelKey]
  };

  if (cards.length) {
    const checkedCards = cards.filter(card => card.checked === true);
    for (const key of Object.keys(productTypeFilters)) {
      const isChecked = getCheckedBoolean(checkedCards, key);
      productTypeFilters[key].checked = isChecked;
    }
  } else {
    filter.checked = !filter.checked;
    productTypeFilters[modelKey] = filter;
  }
  return productTypeFilters;
};
/* When channel is public, and no plan in context use updateFundTypeFilter */
export const updateFundTypeFilter = (state, modelKey, option, cards = []) => {
  let productTypeFilters = {
    ...state.filters.productTypeFilters
  };

  const filter = productTypeFilters[modelKey];
  const presetsCardslength = cards.length;

  if (presetsCardslength) {
    // overwrite productTypeFilters state base on presets cards
    const checkedCards = cards.filter(card => card.checked === true);
    for (const key of Object.keys(productTypeFilters)) {
      const isChecked = getCheckedBoolean(checkedCards, key);
      productTypeFilters[key].checked = isChecked;
    }
    // sub filters will always follow because it's not selectable at presets(option will be all)
    const isCheckedVariableAnnuities = productTypeFilters[PRODUCT_TYPE_MODELS.VARIABLEANNUITIES].checked;
    productTypeFilters = checkVariableAnnuitySubTypes(productTypeFilters, isCheckedVariableAnnuities);
  } else {
    if (filter.options && option && filter.checked) {
      filter.currentOption = option;
    } else {
      filter.checked = !filter.checked;
    }
  }

  if (modelKey === PRODUCT_TYPE_MODELS.VARIABLEANNUITIES) {
    //we need to turn on or off all the sub filters when Variable annuities is checked
    productTypeFilters = checkVariableAnnuitySubTypes(productTypeFilters, filter.checked);
  }

  // if filter from Presets we want to show all the available options
  if (presetsCardslength && modelKey === PRODUCT_TYPE_MODELS.MUTUALFUNDS) {
    productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.ALL;
  }

  return productTypeFilters;
};

const initialState = getInitialState();

/*REDUCER*/
let isBluetagcharacteristicFirstLoad = true; // TIAA-11698, for bluetagcharacteristic parameter
let blueTagObject;
const filterReducer = (state = initialState, action, overwriteIsBluetagcharacteristicFirstLoad = false) => {
  isBluetagcharacteristicFirstLoad = isBluetagcharacteristicFirstLoad || overwriteIsBluetagcharacteristicFirstLoad;
  switch (action.type) {
    case SHOW_ONLY_MY_HOLDINGS: {
      const toggles = { ...state.toggles };
      const showOnlyMyHoldings = !toggles.showOnlyMyHoldings;
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        showOnlyMyHoldings,
        SHOW_ONLY_MY_HOLDINGS
      );

      return {
        ...state,
        toggles: {
          ...state.toggles,
          showOnlyMyHoldings
        },
        nonDefaultActiveFilters
      };
    }

    case SET_INITIAL_STATE: {
      //TIAA-13559 | Re-Designed Screener - Add additional defaultviews
      const productTypeFilters = { ...state.filters.productTypeFilters };
      switch (action.defaultView.toLowerCase()) {
        case DEFAULT_VIEWS.FAONLY:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.FIXEDANNUITIES].checked = true;
          break;
        case DEFAULT_VIEWS.VAONLY:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.VARIABLEANNUITIES].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.CREF].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.CREF].currentOption = SHARE_CLASS.ALL;
          break;
        case DEFAULT_VIEWS.ACCESSONLY:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.VARIABLEANNUITIES].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].currentOption = SHARE_CLASS.ALL;
          break;
        case DEFAULT_VIEWS.MFONLY:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.ALL;
          break;
        case DEFAULT_VIEWS.MFCLASSR6:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.CLASSR6;
          break;
        case DEFAULT_VIEWS.MFRETAILONLY:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.CLASSA;
          break;
        case DEFAULT_VIEWS.MFCLASSI:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.CLASSI;
          break;
        case DEFAULT_VIEWS.MFCLASSPREMIER:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.PREMIER;
          break;
        case DEFAULT_VIEWS.MFCLASSRETIREMENT:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.RETIREMENT;
          break;
        case DEFAULT_VIEWS.MFCLASSW:
          setFilterAllCheckBoxes(state.filters.productTypeFilters, false);
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
          state.filters.productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.CLASSW;
          break;
        default:
          break;
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          productTypeFilters
        }
      };
    }
    case SHOW_ONLY_TIAA: {
      const showOnlyTIAA = !state.filters.showOnlyTIAA;
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        showOnlyTIAA,
        SHOW_ONLY_TIAA
      );

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          showOnlyTIAA
        }
      };
    }
    case SHOW_NULL_DATA: {
      const showFundsWithNullData = !state.filters.showFundsWithNullData;
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        showFundsWithNullData,
        SHOW_NULL_DATA
      );

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          showFundsWithNullData
        }
      };
    }
    case UPDATE_MSTAR_FILTER: {
      // MStarValue should be considered the source of truth, thus use
      // it to update the applied M* filter, as well as in the JSX UI
      const MStarValue = action.value;
      const isActive = MStarValue > -1;
      const MStarValues = [];
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        isActive,
        FIELDS.MSTARRATINGOVERALL
      );

      const max = 6;
      if (isActive) {
        let iterator = action.value + 1;
        while (iterator < max) {
          MStarValues.push(iterator);
          iterator++;
        }
      }
      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          MStarFilterArray: action.MStarArray,
          MStarValue,
          MStarValues,
          presetCategories
        }
      };
    }
    case UPDATE_RANGE_FILTER: {
      const { rangeSelectors, shouldActivate } = updateRangeFilter(
        state,
        action.modelKey,
        action.index
      );
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        shouldActivate,
        action.modelKey
      );

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          rangeSelectors,
          presetCategories
        }
      };
    }
    case UPDATE_STYLE_BOX_FILTER: {
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const styleBoxes = { ...state.filters.styleBoxes };
      const filter = { ...styleBoxes[action.modelKey] };
      const selectors = filter.selectors;
      let shouldActivate = false;

      selectors[action.xIndex][action.yIndex] =
        !selectors[action.xIndex][action.yIndex];

      // Update the filter model
      styleBoxes[action.modelKey] = filter;

      // Loop through arrays to see if any are active for shouldActivate
      selectors.forEach(xAxisArray => {
        if (!shouldActivate && xAxisArray.includes(true)) {
          shouldActivate = true;
        }
      });

      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        shouldActivate,
        action.modelKey
      );

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          styleBoxes,
          presetCategories
        }
      };
    }
    case UPDATE_FUNDTYPE_FILTER: {
      const filterKey = 'productTypeFilters';
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        true,
        filterKey
      );
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const productTypeFilters = updateFundTypeFilter(
        state,
        action.modelKey,
        action.option
      );

      isBluetagcharacteristicFirstLoad = false;
      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          productTypeFilters,
          presetCategories
        }
      };
    }
    case UPDATE_PRODUCT_FILTER: {
      const filterKey = 'productTypeFilters';
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        true,
        filterKey
      );
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const productTypeFilters = updateProductTypeFilter(
        state,
        action.modelKey
      );
      isBluetagcharacteristicFirstLoad = false;

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          productTypeFilters,
          presetCategories
        }
      };
    }
    case UPDATE_ASSET_FILTER: {
      const filterKey = 'assetClassFilters';
      const nonDefaultActiveFilters = updateActiveFilters(
        state,
        true,
        filterKey
      );
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const assetClassFilters = updateAssetClassFilter(state, action.modelKey);

      return {
        ...state,
        nonDefaultActiveFilters,
        filters: {
          ...state.filters,
          assetClassFilters,
          presetCategories
        }
      };
    }
    case UPDATE_PRESET_CATEGORY: {
      const presetCategories = [...state.filters.presetCategories];
      const cards = presetCategories[action.sectionIndex]?.cards;
      const card = cards[action.cardIndex];
      card.checked = !card.checked;

      switch (card.mappings.modelType) {
        case MODEL_TYPES.RANGE_SELECTORS: {
          const { rangeSelectors, shouldActivate } = updateRangeFilter(
            state,
            card.mappings.modelKey,
            card.mappings.modelIndex,
            card.checked
          );
          const nonDefaultActiveFilters = updateActiveFilters(
            state,
            shouldActivate,
            card.mappings.modelKey
          );
          return {
            ...state,
            nonDefaultActiveFilters,
            filters: {
              ...state.filters,
              presetCategories,
              rangeSelectors
            }
          };
        }
        case MODEL_TYPES.MSTAR_SELECTORS: {
          const nonDefaultActiveFilters = updateActiveFilters(
            state,
            card.checked,
            FIELDS.MSTARRATINGOVERALL
          );

          return {
            ...state,
            nonDefaultActiveFilters,
            filters: {
              ...state.filters,
              MStarFilterArray: card.checked ? [true, true, true, true, false] : new Array(5).fill(false),
              MStarValue: card.checked ? 3 : -1,
              MStarValues: card.checked ? ['4', '5'] : [],
              presetCategories
            }
          };
        }
        case MODEL_TYPES.ASSET_CLASS: {
          const assetClassFilters = updateAssetClassFilter(
            state,
            card.mappings.modelKey,
            cards
          );
          const filterKey = 'assetClassFilters';
          const nonDefaultActiveFilters = updateActiveFilters(
            state,
            true,
            filterKey
          );
          return {
            ...state,
            nonDefaultActiveFilters,
            filters: {
              ...state.filters,
              assetClassFilters,
              presetCategories
            }
          };
        }
        case MODEL_TYPES.PRODUCT_TYPE: {
          let productTypeFilters;
          if (
            action.channel === CHANNELS.MYTIAA ||
            state.inputs.isSearchedPlanInContext
          ) {
            productTypeFilters = updateProductTypeFilter(
              state,
              card.mappings.modelKey,
              cards
            );
          } else {
            productTypeFilters = updateFundTypeFilter(
              state,
              card.mappings.modelKey,
              null,
              cards
            );
          }
          const filterKey = 'productTypeFilters';
          const nonDefaultActiveFilters = updateActiveFilters(
            state,
            true,
            filterKey
          );
          return {
            ...state,
            nonDefaultActiveFilters,
            filters: {
              ...state.filters,
              productTypeFilters,
              presetCategories
            }
          };
        }
        default:
          return state;
      }
    }
    case UPDATE_PLAN_NUMBER: {
      const searchedPlanNumber = state.inputs.searchedPlanNumber;

      // Don't change state if planNumber matches what's already in the store
      if (searchedPlanNumber === action.planNumber) {
        return state;
      }

      return {
        ...state,
        inputs: {
          ...state.inputs,
          searchedPlanNumber: action.planNumber,
          subPlan: action.subPlan,
          isSearchedPlanInContext: true,
          isPlanDataLoaded: false
        }
      };
    }
    case FILTER_PLAN_HOLDINGS: {
      const holdings = action.payload;
      // Always start `planXids` with an empty array to clear any previous search
      const planXids = [];
      const planInfo = [];
      const planDisclosures = {};
      let productTypesFromPlan = [];
      let assetClassesFromPlan = [];

      const productTypeFilters = { ...state.filters.productTypeFilters };
      const assetClassFilters = { ...state.filters.assetClassFilters };
      // does this clear out the presets?
      const presetCategories = resetPresetCategories(presetCategoryModels());

      const tryGetSubPlanId = planSet => {
        const holdingWithSubPlan = planSet?.holdings?.find(el => el.annuityProductCode);
        return holdingWithSubPlan ? holdingWithSubPlan.annuityProductCode : '';
      };

      holdings?.forEach(planSet => {
        // eslint-disable-next-line no-unused-expressions
        const singlePlanProductType = planSet?.productTypes;
        if (singlePlanProductType) {
          productTypesFromPlan = [...productTypesFromPlan, ...singlePlanProductType];
        }

        const singlePlanAssetClassType = planSet?.assetClasses;
        if (singlePlanAssetClassType) {
          assetClassesFromPlan = [...assetClassesFromPlan, ...singlePlanAssetClassType];
        }

        planSet?.holdings?.forEach(el => {
          if (el.xid) {
            planXids.push(`${el.xid}`);
          }
          if (el.planLegalName && el.planNumber) {
            planInfo.push(
              {
                name: el.planLegalName,
                number: el.planNumber,
                subPlanId: tryGetSubPlanId(planSet)
              }
            );
          } else if (el.planLegalName) {
            planInfo.push({ name: `${el.planLegalName}` });
          }
          if (el.disclosures) {
            el.disclosures.forEach(disclosure => {
              if (planDisclosures[el.xid] && !planDisclosures[el.xid]?.includes(disclosure.footnoteText)) {
                planDisclosures[el.xid] += `${disclosure.footnoteText}<br><br>`;
              } else {
                planDisclosures[el.xid] = `${disclosure.footnoteText}<br><br>`;
              }
            });
          }
        });
      });

      // Select all products with a product type value in the
      const productFiltersToActivate = getModelKeys(productTypesFromPlan, PRODUCT_TYPE_TO_MODEL);
      if (!blueTagObject) {
        activateFilters(productTypeFilters, productFiltersToActivate);
      }
      // select all asset class that are in the plan
      const assetClassFiltersToActivate = getModelKeys(assetClassesFromPlan, ASSET_CLASS_TO_MODEL);
      if (isBluetagcharacteristicFirstLoad) {
        activateFilters(assetClassFilters, assetClassFiltersToActivate);
      }
      // only show product type and asset class that are included in the selected plan
      presetCategories?.forEach(category => {
        if (category.sectionLabel === 'Asset classes') {
          category.cards = category?.cards?.filter(card => assetClassFiltersToActivate.includes(card.mappings.modelKey));
        } else if (category.sectionLabel === 'Product Type') {
          category.cards = category?.cards?.filter(card => productFiltersToActivate.includes(card.mappings.modelKey));
        }
      });

      return {
        ...state,
        inputs: {
          ...state.inputs,
          isPlanDataLoaded: action.isPlanDataLoaded
        },
        planInfo: _uniqBy(planInfo, plan => JSON.stringify([plan.number, plan.subPlanId])),
        planDisclosures,
        productTypesFromPlan: _uniq(productTypesFromPlan),
        assetClassesFromPlan: _uniq(assetClassesFromPlan),
        filters: {
          ...state.filters,
          productTypeFilters,
          assetClassFilters,
          presetCategories,
          planXids: _uniq(planXids)
        }
      };
    }
    // upon sucessful retrieval of plan info store all user plan info
    case UPDATE_PARTICIPANT_HOLDINGS: {
      return {
        ...state,
        participantHoldings: action.payload,
        participantPlansAndTickers: action.userHoldings
      };
    }

    case UPDATE_SEARCHED_HOLDINGS: {
      return {
        ...state,
        searchedHoldings: action.payload,
        inputs: {
          ...state.inputs,
          isSearchedPlanInContext: true
        }
      };
    }

    case RESET_HOLDINGS_CONTEXT: {
      return {
        ...state,
        inputs: {
          ...state.inputs,
          isSearchedPlanInContext: false,
          searchedPlanNumber: ''
        }
      };
    }

    case RESET_FILTERS: {
      const initialFilters = { ...initialState.filters };
      /*storing away planXids; we don't want to reset them.*/
      const planXids = [...state.filters.planXids];
      /*planXids act as filters and set off a screener request when they change,
      so should remain in the filter object*/
      const presetCategories = resetPresetCategories(presetCategoryModels());
      const rangeSelectors = rangeSelectorModels();
      const styleBoxes = styleBoxModels();
      const productTypeFilters = { ...state.filters.productTypeFilters };
      const assetClassFilters = { ...state.filters.assetClassFilters };
      const isSearchedPlanInContext = state.inputs.isSearchedPlanInContext;

      activateFilters(productTypeFilters, publicDefaultProductTypes);
      activateFilters(assetClassFilters, publicDefaultAssetClasses);
      /*Note, defaultProductTypeFilters change when plan in context */
      if (action.channel !== CHANNELS.PUBLIC || isSearchedPlanInContext) {
        const productTypesFromPlan = state?.productTypesFromPlan;
        const assetClassesFromPlan = state?.assetClassesFromPlan;
        const planDefaultProductTypes = getModelKeys(productTypesFromPlan, PRODUCT_TYPE_TO_MODEL);
        const planDefaultAssetClasses = getModelKeys(assetClassesFromPlan, ASSET_CLASS_TO_MODEL);
        activateFilters(productTypeFilters, planDefaultProductTypes);
        activateFilters(assetClassFilters, planDefaultAssetClasses);
      }

      return {
        ...state,
        nonDefaultActiveFilters: [],
        toggles: {
          showOnlyMyHoldings: false
        },
        filters: {
          ...initialFilters,
          productTypeFilters,
          assetClassFilters,
          presetCategories,
          rangeSelectors,
          styleBoxes,
          planXids,
          showFundsWithNullData: false,
          showOnlyTIAA: false
        }
      };
    }
    case FETCH_PLAN_REQUEST:
      return {
        ...state,
        planApiLoading: true
      };
    case FETCH_PLAN_SUCCESS: {
      return {
        ...state,
        planApiLoading: false,
        planApiError: ''
      };
    }
    case FETCH_PLAN_FAILURE:
      return {
        ...state,
        planApiLoading: false,
        planApiError: action.payload
      };

    case REDUCE_ARGS: {
      const statefilters = { ...state.filters };
      let MStarValues = [...statefilters.MStarValues];
      let MStarFilterArray = [...statefilters.MStarFilterArray];
      const rangeFilters = { ...statefilters.rangeSelectors };
      const assetClassFilters = statefilters.assetClassFilters;
      const appliedFilters = { ...state.args.appliedFilters };
      const planXids = [...state.filters.planXids];
      const styleBoxes = statefilters.styleBoxes;
      const bondBoxes = { ...styleBoxes[STYLE_BOX_MODELS.BONDS] };
      const equityBoxes = { ...styleBoxes[STYLE_BOX_MODELS.EQUITIES] };
      let bondBoxSelectors = bondBoxes.selectors;
      let equityBoxSelectors = equityBoxes.selectors;
      const isSearchedPlanInContext = state.inputs.isSearchedPlanInContext;
      const isShowTIAAFunds = state.filters.showOnlyTIAA;
      const isShowOnlyNull = state.filters.showFundsWithNullData;
      let productTypeFilters = statefilters.productTypeFilters;
      const bluetagcharacteristic = action?.bluetagcharacteristic || null;
      blueTagObject = queryStringMap[bluetagcharacteristic?.toLowerCase()];
      const tempftArgs = {};
      let fundTypeArgs = {};
      // A map of StyleBox screener input values that
      // correspond to the X & Y indexes.
      const styleBoxValueMap = [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9]
      ];

      const assetValues = [];
      const productValues = [];
      const equityValues = [];
      const bondValues = [];

      // TIAA-11698>>> screener URL with bluetagcharacteristic parameter
      const setAllProductTypeFilters = () => {
        productTypeFilters[PRODUCT_TYPE_MODELS.CREF].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.CREF].currentOption = SHARE_CLASS.ALL;
        productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].currentOption = SHARE_CLASS.ALL;
        productTypeFilters[PRODUCT_TYPE_MODELS.TIAAREALESTATE].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].currentOption = SHARE_CLASS.ALL;
        productTypeFilters[PRODUCT_TYPE_MODELS.FDICINSUREDPRODUCTS].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.OTHER].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.RETIREPLUSMODELS].checked = true;
        productTypeFilters[PRODUCT_TYPE_MODELS.RETIREPROPLUSMODELS].checked = true;
      }
      if (blueTagObject && isBluetagcharacteristicFirstLoad) {
        switch (blueTagObject.filter) {
          case 'productTypeFilters':
            productTypeFilters = setObjectValues(statefilters.productTypeFilters, 'checked', false);
            if (productTypeFilters.hasOwnProperty(blueTagObject.filterValue)) {
              productTypeFilters[blueTagObject.filterValue].checked = true;
              if (blueTagObject.classSelection) {
                //Set shareclass to All
                productTypeFilters[blueTagObject.filterValue].currentOption = blueTagObject.classSelection;
              }
              if (blueTagObject.filterValue === PRODUCT_TYPE_MODELS.VARIABLEANNUITIES) {
                //Make sure all VA subtypes are checked
                productTypeFilters[PRODUCT_TYPE_MODELS.CREF].checked = true;
                productTypeFilters[PRODUCT_TYPE_MODELS.CREF].currentOption = SHARE_CLASS.ALL;
                productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].checked = true;
                productTypeFilters[PRODUCT_TYPE_MODELS.TIAAACCESS].currentOption = SHARE_CLASS.ALL;
                productTypeFilters[PRODUCT_TYPE_MODELS.TIAAREALESTATE].checked = true;
              }
            }
            break;
          case 'rangeFilters':
            //Range filters include Performance, Performance-vs-Peers, Risk, and Yield. blueTagObject modelKey defines which
            updateRangeFilter(state, blueTagObject?.modelKey, blueTagObject?.range);
            setAllProductTypeFilters();
            isBluetagcharacteristicFirstLoad = false;
            break;
          case 'ratingsFilter':
            //4 stars
            MStarValues = [4, 5];
            MStarFilterArray = [true, true, true, true, false];
            state.filters.MStarFilterArray = MStarFilterArray;
            setAllProductTypeFilters();
            isBluetagcharacteristicFirstLoad = false;
            break;

          //TIAA-13458, Re-designed Screener: Add Asset Class Types to '?bluetagcharacteristic'
          case 'assetClassFilters':
            updateAssetClassFilterByBlueTag(state, blueTagObject.modelKey);
            setAllProductTypeFilters();
            isBluetagcharacteristicFirstLoad = false;
            break;

          //TIAA-13697, Re-designed Screener: Add Bond Style Box to '?bluetagcharacteristic'
          case 'styleBoxesBonds':
            setAllProductTypeFilters();
            bondBoxSelectors = updateSytleBoxesByBlueTag(blueTagObject.modelKey);
            state.filters.styleBoxes.bondsModel.selectors = bondBoxSelectors;
            isBluetagcharacteristicFirstLoad = false;
            break;

          //TIAA-13698, Re-designed Screener: Add Equities Style Box to '?bluetagcharacteristic'
          case 'styleBoxesEquities':
            setAllProductTypeFilters();
            equityBoxSelectors = updateSytleBoxesByBlueTag(blueTagObject.modelKey);
            state.filters.styleBoxes.equitesModel = equityBoxSelectors;
            state.filters.styleBoxes.equitiesModel.selectors = equityBoxSelectors;
            isBluetagcharacteristicFirstLoad = false;
            break;

          case 'expenseFilters':

            updateExpenseFilter(state, blueTagObject?.modelKey, blueTagObject?.range);
            setAllProductTypeFilters();
            isBluetagcharacteristicFirstLoad = false;
            break;
          default:
            break;
        }
      }
      // TIAA-11698>>> screener URL with bluetagcharacteristic parameter

      if (action.channel !== CHANNELS.PUBLIC || isSearchedPlanInContext) {
        //when a plan is in context (329) we send product type filters
        for (const filterObj of Object.values(productTypeFilters)) {
          if (filterObj.checked === true && filterObj.productTypeValue) {
            productValues.push(filterObj.productTypeValue);
          }
        }

        if (productValues.length > 0) {
          appliedFilters[FIELDS.PRODUCTTYPE] = new Conditions().like(
            productValues
          ).conditionList;
        } else {
          delete appliedFilters[FIELDS.PRODUCTTYPE];
        }

      } else {
        //we switched to using fund type (296) and dont want product type args passed

        if (appliedFilters.hasOwnProperty([FIELDS.PRODUCTTYPE])) {
          delete appliedFilters[FIELDS.PRODUCTTYPE];
        }

        const shareClassArgs = [];
        for (const filterObj of Object.values(productTypeFilters)) {
          if (filterObj.checked === true && filterObj.fundTypeValue) {
            if (filterObj.options) {
              shareClassArgs.push(
                formatShareClassArgs(
                  filterObj.fundTypeValue,
                  filterObj.currentOption
                )
              );
            } else {
              //we don't want to push any product type that doesnt have a corresponding fund type
              productValues.push(filterObj.fundTypeValue);
            }
          }
        }

        if (productValues.length > 0) {
          tempftArgs[FIELDS.TCFUNDTYPE] = new Conditions().like(
            productValues
          ).conditionList;
        } else {
          delete tempftArgs[FIELDS.TCFUNDTYPE];
        }

        const ftArgs = formatArgs(tempftArgs);

        for (const nestedArg of shareClassArgs) {
          ftArgs.push(nestedArg);
        }

        fundTypeArgs = formatFundTypeArgs(ftArgs);
      }

      if (planXids.length > 0) {
        appliedFilters[FIELDS.TCWSODISSUE] = new Conditions().equalTo(
          planXids
        ).conditionList;
      } else {
        delete appliedFilters[FIELDS.TCWSODISSUE];
      }

      if (MStarValues.length > 0) {
        appliedFilters[FIELDS.MSTARRATINGOVERALLNULL] =
          new Conditions().equalTo(MStarValues).conditionList;
      } else {
        delete appliedFilters[FIELDS.MSTARRATINGOVERALLNULL];
      }

      if (isShowTIAAFunds) {
        appliedFilters[FIELDS.TCTREATASPROP] =
          new Conditions().equalTo([1]).conditionList;
      } else {
        delete appliedFilters[FIELDS.TCTREATASPROP];
      }

      // eslint-disable-next-line array-callback-return
      Object.values(rangeFilters).forEach(filter => {
        const rangeValues = [];
        filter.selectors.forEach((val, index) => {
          if (val === true) rangeValues.push(index + 1);
        });

        if (rangeValues.length > 0) {
          if (filter.fieldKey === FIELDS.TCINCEPTIONDATE) {
            const conditions = new Conditions();
            rangeValues.forEach(value => {
              if (value === 1) {
                return conditions.lessThanEqualTo(['36525']);
              }
              if (value === 2) {
                return conditions
                  .greaterThan(['36525'])
                  .lessThanEqualTo(['40178']);
              }
              if (value === 3) {
                return conditions
                  .greaterThan(['40178'])
                  .lessThanEqualTo(['43830']);
              }
              return conditions.greaterThanEqualTo(['43831']);
            });
            appliedFilters[filter.fieldKey] = conditions.conditionList;
          } else {
            appliedFilters[filter.fieldKey] = new Conditions().equalTo(
              rangeValues
            ).conditionList;
          }
        } else {
          delete appliedFilters[filter.fieldKey];
        }
        return null;
      });

      bondBoxSelectors.forEach((xValue, xIndex) => {
        xValue.forEach((yValue, yIndex) => {
          if (yValue === true) {
            bondValues.push(styleBoxValueMap[xIndex][yIndex]);
          }
        });
      });

      if (bondValues.length > 0) {
        appliedFilters[FIELDS.MSTARBONDBOXFILTER] = new Conditions().equalTo(
          bondValues
        ).conditionList;
      } else {
        delete appliedFilters[FIELDS.MSTARBONDBOXFILTER];
      }

      equityBoxSelectors.forEach((xValue, xIndex) => {
        xValue.forEach((yValue, yIndex) => {
          if (yValue === true) {
            equityValues.push(styleBoxValueMap[xIndex][yIndex]);
          }
        });
      });

      if (equityValues.length > 0) {
        appliedFilters[FIELDS.MSTAREQUITYBOXFILTER] = new Conditions().equalTo(
          equityValues
        ).conditionList;
      } else {
        delete appliedFilters[FIELDS.MSTAREQUITYBOXFILTER];
      }

      for (const filterObj of Object.values(assetClassFilters)) {
        if (
          filterObj.checked === true &&
          filterObj.modelKey !== ASSET_CLASS_MODELS.ALL
        ) {
          assetValues.push(filterObj.value);
        }
      }

      if (assetValues.length > 0) {
        appliedFilters[FIELDS.ASSETCLASS] = new Conditions().like(
          assetValues
        ).conditionList;
      } else {
        delete appliedFilters[FIELDS.ASSETCLASS];
      }

      if (isShowOnlyNull) {
        for (const key in appliedFilters) {
          if (key !== FIELDS.ASSETCLASS && key !== FIELDS.PRODUCTTYPE && appliedFilters[key]) {
            appliedFilters[key][0]?.values?.push('-32768');
          }
        }
      }


      return {
        ...state,
        args: {
          appliedFilters,
          fundTypeArgs
        }
      };
    }
    case REDUCE_ARGS_DISTRIBUTIONS: {
      const statefilters = { ...state.filters };
      const productTypeFilters = statefilters.productTypeFilters;
      const appliedFilters = { ...state.args.appliedFilters };
      const planXids = [...state.filters.planXids];
      const isSearchedPlanInContext = state.inputs.isSearchedPlanInContext;

      const tempftArgs = {};
      let fundTypeArgs = {};

      const productValues = [];

      if (action.channel !== CHANNELS.PUBLIC || isSearchedPlanInContext) {
        //for the distributions we only want to return mutual funds so just push mutual funds
        productValues.push(productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].productTypeValue);

        if (productValues.length > 0) {
          appliedFilters[FIELDS.PRODUCTTYPE] = new Conditions().like(
            productValues
          ).conditionList;
        } else {
          delete appliedFilters[FIELDS.PRODUCTTYPE];
        }
      } else {
        //we switched to using fund type (296) and dont want product type args passed
        if (appliedFilters.hasOwnProperty([FIELDS.PRODUCTTYPE])) {
          delete appliedFilters[FIELDS.PRODUCTTYPE];
        }

        productValues.push(productTypeFilters[PRODUCT_TYPE_MODELS.MUTUALFUNDS].fundTypeValue);

        if (productValues.length > 0) {
          tempftArgs[FIELDS.TCFUNDTYPE] = new Conditions().like(
            productValues
          ).conditionList;
        } else {
          delete tempftArgs[FIELDS.TCFUNDTYPE];
        }

        const ftArgs = formatArgs(tempftArgs);

        fundTypeArgs = formatFundTypeArgs(ftArgs);
      }

      if (planXids.length > 0) {
        appliedFilters[FIELDS.TCWSODISSUE] = new Conditions().equalTo(
          planXids
        ).conditionList;
      } else {
        delete appliedFilters[FIELDS.TCWSODISSUE];
      }
      return {
        ...state,
        args: {
          appliedFilters,
          fundTypeArgs
        }
      };
    }
    default:
      return state;
  }
};

export default filterReducer;
